import React from 'react';
import { Heart } from 'lucide-react';
import HeroImage from '../components/HeroImage';
import AnimatedSection from '../components/AnimatedSection';

const testimonials = [
  {
    quote: "Our first interaction with Z was incredible. It was clear from the start that Z and Team Atlas had done their homework on our industry. They delivered a website that was not only visually stunning but also built with SEO at its core. They took care of everything, making the process stress-free. The increase in traffic and leads has been worth every penny. If you're looking for a team that combines creativity and results, go with Atlas AI!",
    author: "Elliot Giles",
    role: "CTO",
    industry: "E-commerce",
    image: "https://images.unsplash.com/photo-1568602471122-7832951cc4c5?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "As a small business owner, I needed a website that could compete with much larger companies, and Team Atlas delivered. Christoph and Z transformed my outdated site into a sleek, modern design that's already generating double the leads. Their focus on SEO has made a huge impact on our online presence. I couldn't recommend Atlas AI more highly!",
    author: "Emily Rodriguez",
    role: "Director",
    industry: "Hospitality",
    image: "https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "Our social media presence was non-existent before hearing about Atlas. Their team put together a structured content plan, automated engagement using AI, and helped us create posts that actually get traction, almost out of thin air. Our brand finally has a real online presence, and it's driving sales. Highly recommend.",
    author: "Jimmy Wilson",
    role: "Research Director",
    industry: "Market Research",
    image: "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "What a blessing Atlas AI has been in building our new ministry website! Jay and the team were professional, responsive, and made the entire process smooth and educational. We had a lot of questions about site security, donations, and other details, and they answered everything thoroughly. The end result was a beautiful, responsive website at a fair price. We'd gladly work with them again!",
    author: "Krista Masterson",
    role: "Operations Manager",
    industry: "Logistics",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "Atlas AI has been a game-changer for us. Mauricio and Christoph are amazing to work with—they truly care about delivering results. Thanks to their work, our Google Ads campaigns are performing better than ever, and our revenue has soared. I wouldn't hesitate to recommend them to anyone.",
    author: "Marc Thompson",
    role: "Customer Success Lead",
    industry: "SaaS",
    image: "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "When fall came around, we wanted to generate more leads for our lawn care services, and Atlas AI made it happen. Jay and Mauricio were amazing at targeting the right audience at the right time, and we saw a massive boost in leads. Their expertise made all the difference. Atlas is an essential partner for anyone in a competitive industry!",
    author: "Sofia Kruger",
    role: "Production Manager",
    industry: "Manufacturing",
    image: "https://images.unsplash.com/photo-1598550874175-4d0ef436c909?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "AI was pretty intimidating for our staff at first but Z made the transition effortless. He provided hands-on training, clear explanations, and step-by-step guidance on using the ACSS module and AI phone assistant. Our employees now confidently handle automation. They are dedicated to 'getting results', and every single penny we paid so far has been worth it.",
    author: "Daryush Khan",
    role: "Analytics Director",
    industry: "Finance",
    image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "Our lead generation was inconsistent until we partnered with Atlas AI Business Solutions. Christoph and the team revamped our strategy, added AI chatbots that engage visitors in real-time, and optimized our online presence. The results? A steady flow of high-quality leads every day. We're beyond impressed!",
    author: "Rachel Foster",
    role: "Medical Director",
    industry: "Healthcare",
    image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&q=80&w=200&h=200"
  },
  {
    quote: "Team Atlas designed our new website, and it's everything we hoped for. They were thorough, responsive, and made revisions to meet our needs, even with the complexity of our organization. Z and Christoph excelled in understanding our audience and helping us hit our targets. The website has been a big success, and we can't wait to work with Atlas again in the future.",
    author: "Emma Martinez",
    role: "Marketing Director",
    industry: "Digital Marketing",
    image: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=200&h=200"
  }
];

function Testimonials() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&q=80&w=1920&h=1080" 
              alt="Business team meeting"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-5xl font-bold text-center text-white mb-6">Client Success Stories</h1>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              Atlas is Always Active, because Atlas gets results. But don't take our word for it! Take a look at what our clients have to say.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <div className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <AnimatedSection key={index}>
              <TestimonialCard {...testimonial} />
            </AnimatedSection>
          ))}
        </div>
      </div>
    </div>
  );
}

function TestimonialCard({ quote, author, role, industry, image }) {
  return (
    <div className="white-holographic-card p-8 rounded-xl">
      <div className="flex items-center mb-6">
        <img src={image} alt={author} className="w-12 h-12 rounded-full object-cover" />
        <div className="ml-4">
          <h3 className="font-semibold text-gray-800">{author}</h3>
          <p className="text-gray-600 text-sm">{role}</p>
          <p className="text-[#00ffff] text-sm">{industry}</p>
        </div>
      </div>
      <p className="text-gray-700">{quote}</p>
    </div>
  );
}

export default Testimonials;